import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  User, LogOut, Settings, CreditCard, MessageSquare, 
  HelpCircle, Download, ChevronDown, UserPlus, LogIn, Crown, Sparkles
} from 'lucide-react';

export default function UserMenu() {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSignOut = async () => {
    setIsOpen(false);
    await logout();
    navigate('/');
  };

  const handleNavigate = (path) => {
    setIsOpen(false);
    navigate(path);
  };

  // Get user initials for avatar
  const getInitials = () => {
    if (!user?.name) return '?';
    const names = user.name.split(' ');
    if (names.length >= 2) {
      return `${names[0][0]}${names[1][0]}`.toUpperCase();
    }
    return user.name[0].toUpperCase();
  };

  // Get subscription tier from user data
  const getUserTier = () => {
    return user?.subscription?.tier || 'free';
  };

  return (
    <div className="relative" ref={menuRef}>
      {/* Avatar Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 p-1 rounded-full hover:bg-secondary transition-colors"
        aria-label="User menu"
      >
        {user ? (
          <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold">
            {getInitials()}
          </div>
        ) : (
          <div className="w-8 h-8 rounded-full bg-secondary text-muted-foreground flex items-center justify-center">
            <User className="w-4 h-4" />
          </div>
        )}
        <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-64 bg-card border border-border rounded-xl shadow-lg overflow-hidden z-50">
          {user ? (
            // Signed-in user menu
            <>
              {/* User Info Header */}
              <div className="px-4 py-3 bg-secondary/30 border-b border-border">
                <p className="font-semibold text-sm truncate">{user.name}</p>
                <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                <div className="flex items-center gap-1.5 mt-1">
                  {getUserTier() === 'pro' ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary/20 text-primary text-xs rounded-full">
                      <Crown className="w-3 h-3" />
                      Pro
                    </span>
                  ) : getUserTier() === 'lifetime_pro' ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-500/20 text-amber-500 text-xs rounded-full">
                      <Crown className="w-3 h-3" />
                      Lifetime Pro
                    </span>
                  ) : getUserTier() === 'basic' ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-500/20 text-blue-500 text-xs rounded-full">
                      <Sparkles className="w-3 h-3" />
                      Basic
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-secondary text-muted-foreground text-xs rounded-full">
                      Free
                    </span>
                  )}
                </div>
              </div>

              {/* Upgrade CTA for free/basic users - hide for pro and lifetime_pro */}
              {getUserTier() !== 'pro' && getUserTier() !== 'lifetime_pro' && (
                <div className="px-4 py-2 border-b border-border">
                  <button
                    onClick={() => handleNavigate('/pricing')}
                    className="w-full px-3 py-2 bg-primary/10 text-primary text-sm font-medium rounded-lg hover:bg-primary/20 transition-colors flex items-center justify-center gap-2"
                  >
                    <Crown className="w-4 h-4" />
                    {getUserTier() === 'free' ? 'Upgrade to Pro' : 'Upgrade'}
                  </button>
                </div>
              )}

              {/* Menu Items */}
              <div className="py-2">
                <button
                  onClick={() => handleNavigate('/profile')}
                  className="w-full px-4 py-2.5 flex items-center gap-3 text-sm hover:bg-secondary transition-colors text-left"
                >
                  <User className="w-4 h-4 text-muted-foreground" />
                  My Profile
                </button>
                
                <button
                  onClick={() => handleNavigate('/subscription')}
                  className="w-full px-4 py-2.5 flex items-center gap-3 text-sm hover:bg-secondary transition-colors text-left"
                >
                  <CreditCard className="w-4 h-4 text-muted-foreground" />
                  Manage Subscription
                </button>

                {(getUserTier() === 'pro' || getUserTier() === 'lifetime_pro') && (
                  <button
                    onClick={() => toast.info('CSV Export coming soon! 📊')}
                    className="w-full px-4 py-2.5 flex items-center gap-3 text-sm hover:bg-secondary transition-colors text-left text-muted-foreground"
                  >
                    <Download className="w-4 h-4" />
                    <span className="flex-1">Export Data</span>
                    <span className="text-[10px] px-1.5 py-0.5 bg-amber-500/20 text-amber-600 rounded">SOON</span>
                  </button>
                )}

                <button
                  onClick={() => handleNavigate('/settings')}
                  className="w-full px-4 py-2.5 flex items-center gap-3 text-sm hover:bg-secondary transition-colors text-left"
                >
                  <Settings className="w-4 h-4 text-muted-foreground" />
                  Settings
                </button>
              </div>

              {/* Feedback & Support */}
              <div className="py-2 border-t border-border">
                <button
                  onClick={() => handleNavigate('/feedback')}
                  className="w-full px-4 py-2.5 flex items-center gap-3 text-sm hover:bg-secondary transition-colors text-left"
                >
                  <MessageSquare className="w-4 h-4 text-muted-foreground" />
                  Feedback
                </button>
                
                <button
                  onClick={() => handleNavigate('/contact')}
                  className="w-full px-4 py-2.5 flex items-center gap-3 text-sm hover:bg-secondary transition-colors text-left"
                >
                  <HelpCircle className="w-4 h-4 text-muted-foreground" />
                  Contact / Support
                </button>
              </div>

              {/* Sign Out */}
              <div className="py-2 border-t border-border">
                <button
                  onClick={handleSignOut}
                  className="w-full px-4 py-2.5 flex items-center gap-3 text-sm text-red-500 hover:bg-red-500/10 transition-colors text-left"
                >
                  <LogOut className="w-4 h-4" />
                  Sign Out
                </button>
              </div>
            </>
          ) : (
            // Guest/unsigned-in user menu
            <>
              {/* Sign In / Sign Up - Separate dedicated flows */}
              <div className="py-2">
                <button
                  onClick={() => handleNavigate('/auth?mode=signin')}
                  className="w-full px-4 py-2.5 flex items-center gap-3 text-sm font-medium text-primary hover:bg-primary/10 transition-colors text-left"
                >
                  <LogIn className="w-4 h-4" />
                  Sign In
                </button>
                
                <button
                  onClick={() => handleNavigate('/auth?mode=signup')}
                  className="w-full px-4 py-2.5 flex items-center gap-3 text-sm font-medium hover:bg-secondary transition-colors text-left"
                >
                  <UserPlus className="w-4 h-4 text-muted-foreground" />
                  Sign Up
                </button>
              </div>

              {/* Pricing Link */}
              <div className="py-2 border-t border-border">
                <button
                  onClick={() => handleNavigate('/pricing')}
                  className="w-full px-4 py-2.5 flex items-center gap-3 text-sm hover:bg-secondary transition-colors text-left"
                >
                  <Crown className="w-4 h-4 text-muted-foreground" />
                  View Plans & Pricing
                </button>
              </div>

              {/* Feedback & Support */}
              <div className="py-2 border-t border-border">
                <button
                  onClick={() => handleNavigate('/feedback')}
                  className="w-full px-4 py-2.5 flex items-center gap-3 text-sm hover:bg-secondary transition-colors text-left"
                >
                  <MessageSquare className="w-4 h-4 text-muted-foreground" />
                  Feedback
                </button>
                
                <button
                  onClick={() => handleNavigate('/contact')}
                  className="w-full px-4 py-2.5 flex items-center gap-3 text-sm hover:bg-secondary transition-colors text-left"
                >
                  <HelpCircle className="w-4 h-4 text-muted-foreground" />
                  Contact / Support
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
